import { inject, injectable } from 'inversify';
import { Observable, PureComputed, Subscribable } from 'knockout';
import { wccModules } from '../../../enums/wccModules';
import { instance as ctx } from 'services/context';
import './twofactorauthsetup.html';
import './twofactorauthsetup.scss';
import ko from '../../../extensions/knockout/extended';
import { CookieHelpers } from '../../../helpers/cookie';

declare var $: any;

export enum TwoFactorAuthTypes {
    None = 0x00,
    Email = 0x01,
    Sms = 0x02,
    Authenticator = 0x04
}

interface TwoFactorAuthSetupVMConfig {
    twoFactorAuthOptions: Observable<TwoFactorAuthTypes>;
    twoFactorAuthChoice: Observable<TwoFactorAuthTypes>;
    includeTitle: boolean | undefined;
    setInitialState: Observable<boolean>;
    passwordLessOption?: boolean;
    embed: boolean | undefined; // whether verify is embedded in another page/component
}

components.register('qrcode-generator', () => require('components/qrcodeGenerator/qrcodeGenerator'));

@injectable()
export default class TwoFactorAuthSetup {

    labels = labels;
    messages = messages;

    labelSetupDescription: PureComputed<string>;
    labelSetupMultipleOptions: PureComputed<string>;

    twoFactorAuthOptions: Observable<TwoFactorAuthTypes>;
    authChoice: Observable<TwoFactorAuthTypes>;
    twoFactorAuthIsForced: PureComputed<boolean>;
    taskSiteSetup: PureComputed<boolean>;   // whether this is task site setup or not as things change here

    includeTitle: boolean;

    modifyChoiceInitialState: boolean;
    
    modifyChoice: Observable<boolean>;
    removedTwoFactorAuth: Observable<boolean>;
    twoFactorAuthChoiceText: PureComputed<string>;

    passwordInput: Observable<string> = ko.observable('');
    passwordValidating: Observable<boolean> = ko.observable<boolean>(false);
    passwordAttemptFail: Observable<string> = ko.observable('');
    modalWindowId: string = 'passwordModal_' + WCC.System.generateHashString(10);

    showEmail: PureComputed<boolean>;
    showAuthenticator: PureComputed<boolean>;
    onlyShowOneOption: PureComputed<boolean>;

    // view state options
    setupNewTwoFactorAuth: PureComputed<boolean>;
    modifyExistingTwoFactorAuth: PureComputed<boolean>;
    showModifyOptions: PureComputed<boolean>;

    // authenticator setup options
    showAuthenticatorSetup = ko.observable<boolean>(false);
    qrCodeGeneratorConfig = ko.observable<any | undefined>();
    onValidAuthenticatorVerification = ko.observable<boolean>(false);
    authenticatorType = ko.pureComputed(() => TwoFactorAuthTypes.Authenticator);
    
    hasMobile: PureComputed<boolean>;

    error: Observable<boolean>;
    busy: Observable<boolean>;

    embed: boolean;

    constructor(
        @inject(wccModules.componentConfig) config: TwoFactorAuthSetupVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        if (_.isUndefined(config.twoFactorAuthOptions) || !config.twoFactorAuthOptions()) throw Error('Invalid configuration - must provide auth options');

        this.twoFactorAuthIsForced = ko.pureComputed(() => WCC.getKOData('forceTwoFactorAuth') ?? false);
        this.taskSiteSetup = ko.pureComputed(() => WCC.getSetting('taskSiteSetup') ?? false)
        this.twoFactorAuthOptions = ko.observable<TwoFactorAuthTypes>(config.twoFactorAuthOptions());
        
        this.authChoice = ko.observable<TwoFactorAuthTypes>(config.twoFactorAuthChoice() ?? TwoFactorAuthTypes.None);
        
        this.modifyChoiceInitialState = config.twoFactorAuthChoice() > 0;
        
        this.modifyChoice = ko.observable<boolean>(config.twoFactorAuthChoice() > 0);

        // on a successful result we update the validated result observable provided by the parent view
        this.authChoice.subscribe((value: TwoFactorAuthTypes) => {
            //console.log(value);
            config.twoFactorAuthChoice(value);
        });

        this.hasMobile = ko.pureComputed(() => WCC.getSetting('twoFactorAuthPersonHasMobile'));

        this.includeTitle = config.includeTitle ?? false; // default to title being off
        this.embed = config.embed ?? false;

        this.showEmail = ko.pureComputed<boolean>(() => (this.twoFactorAuthOptions() & TwoFactorAuthTypes.Email) == TwoFactorAuthTypes.Email);
        /*this.showSms = ko.pureComputed<boolean>(() => (this.twoFactorAuthOptions() & TwoFactorAuthTypes.Sms) == TwoFactorAuthTypes.Sms && this.hasMobile());*/
        this.showAuthenticator = ko.pureComputed<boolean>(() => (this.twoFactorAuthOptions() & TwoFactorAuthTypes.Authenticator) == TwoFactorAuthTypes.Authenticator);
        this.onlyShowOneOption = ko.pureComputed<boolean>(() => !(this.showEmail() && this.showAuthenticator()));

        this.setupNewTwoFactorAuth = ko.pureComputed<boolean>(() => !this.modifyChoice() && !this.showAuthenticatorSetup());
        this.modifyExistingTwoFactorAuth = ko.pureComputed<boolean>(() => this.modifyChoice());
        this.showModifyOptions = ko.pureComputed<boolean>(() => !this.removedTwoFactorAuth());
        
        // Subscribe to the paramater provided to reset initial state
        config.setInitialState?.subscribe((resetState: boolean) => {

            //console.log('resetState: ', resetState, 'authChoice: ', this.authChoice(), 'modifyChoice: ', this.modifyChoice(), 'externalAuthChoice: ', config.twoFactorAuthChoice());

            if (resetState) {
                // on a reset state we need to reset our choice to the external value
                if (config.twoFactorAuthChoice() != this.authChoice()) {
                    this.authChoice(config.twoFactorAuthChoice() ?? TwoFactorAuthTypes.None);
                }

                // reset our state
                this.modifyChoice(this.authChoice() > 0);
                this.removedTwoFactorAuth(false);
                this.showAuthenticatorSetup(false);
                
                // reset our value(so we can activate again)
                config.setInitialState(false);
            }
        });

        // subscribe to our verify of authenticator code. If successful, we need to save our choice which will trigger next steps
        this.onValidAuthenticatorVerification.subscribe(async (value: boolean) => {
            //console.log('onValidAuthenticatorVerification: ', value, 'authChoice: ', this.authChoice(), 'modifyChoice: ', this.modifyChoice());

            if (value) {
                await this.setupTwoFactorAuth(TwoFactorAuthTypes.Authenticator);                
            }
        });

        let passwordLess = config.passwordLessOption;

        this.labelSetupDescription = ko.pureComputed(() => passwordLess ? labels.MFA_SetupDescriptionPasswordLess : labels.MFA_SetupDescription);
        this.labelSetupMultipleOptions = ko.pureComputed(() => passwordLess ? labels.MFA_SetupMultipleOptionsPasswordLess : labels.MFA_SetupMultipleOptions);

        this.twoFactorAuthChoiceText = ko.pureComputed<string>(() => {
            let computedLabel = labels.MFA_SetupSendByEmail;

            switch (this.authChoice()) {
                case TwoFactorAuthTypes.Sms: {
                    computedLabel = labels.MFA_SetupSendBySMS;
                    break;
                }
                case TwoFactorAuthTypes.Authenticator: {
                    computedLabel = labels.MFA_SetupSendByAuthenticator;
                    break;
                }
                case TwoFactorAuthTypes.Email:
                default: {
                    computedLabel = labels.MFA_SetupSendByEmail;
                    break;
                }
            } 
            return computedLabel;            
        });
        this.removedTwoFactorAuth = ko.observable<boolean>(false);

        this.error = ko.observable<boolean>(false);
        this.busy = ko.observable<boolean>(false);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'wcc-two-factor-auth-setup-standalone': !this.embed
            }
        });
    }

    async init() {
        // if task site and only email as option, auto setup
        if (this.taskSiteSetup()) {

            // Base on our 2FA options, if we only have email and sms - default to email
            // If options are email and authenticator, show them setup
            var tfaOptions = this.twoFactorAuthOptions();

            let allowsEmail = (tfaOptions & TwoFactorAuthTypes.Email) == TwoFactorAuthTypes.Email;
            let allowsSms = (tfaOptions & TwoFactorAuthTypes.Sms) == TwoFactorAuthTypes.Sms;
            let allowsAuthenticator = (tfaOptions & TwoFactorAuthTypes.Authenticator) == TwoFactorAuthTypes.Authenticator;

            // bit wise check to see if we have email or sms as options and no authenticator
            if ((allowsEmail || allowsSms) && !allowsAuthenticator) {
                // set us up as email (as SMS is an option only if mobile present)
                await this.setupTwoFactorAuth(TwoFactorAuthTypes.Email);
            }
        }
    }

    async setupTwoFactorAuthForEmail() {
        await this.setupTwoFactorAuth(TwoFactorAuthTypes.Email);
    }

    async setupTwoFactorAuthForAuthenticator() {
        var result = await ctx.twoFactorAuthService.setupAuthenticator();

        // generate QR code with result.uri
        this.qrCodeGeneratorConfig({
            QRCodeHeader: labels.MFA_SetupTitleQRCode,
            QRCodeSubHeader: result.SecretKey,
            QRCodeSourceString: result.Uri,
            QRCodeClassName: 'tfa-authenticator-qr-code',
            QRCodeCopyLinkText: labels.MFA_SetupQRCodeCopy
        });

        this.showAuthenticatorSetup(true);
    }

    async setupTwoFactorAuthForSms() {
        await this.setupTwoFactorAuth(TwoFactorAuthTypes.Sms);
    }

    /**
     * Go and update our choice for two factor auth
     * @param choice
     */
    async setupTwoFactorAuth(choice: TwoFactorAuthTypes) {
        
        this.busy(true);

        try {
            var savedChoice = await ctx.twoFactorAuthService.setupTwoFactorAuth({ authType: choice });

            // don't delete for authenticator as we need to keep the cookie for the setup process (to stop secondary verification)
            if (choice != TwoFactorAuthTypes.Authenticator) {
                // delete any 2FA cookie we have currently. This is to make sure 2FA is enforced again after setup otherwise leaving or refreshing ignores the verification
                CookieHelpers.deleteCookie(CookieHelpers.QUALZY_COOKIE_2FA);
            }

            // update our subscribed observable to indicate component success result (parent view should be subscribed
            // to this observable and trigger accordingly)
            this.authChoice(savedChoice);
        }
        catch (error) {
            console.log(error);
            // shows a generic error message for the user
            this.error(true);
        }
        finally {
            this.busy(false);            
        }
    }

    modifyTwoFactorAuth() {
        //reset our auth option to setup
        this.modifyChoice(false);
        this.authChoice(TwoFactorAuthTypes.None);
    }

    removeTwoFactorAuth() {
        this.passwordAttemptFail('');
        this.showPasswordChallenge();
    }

    async removeTwoFactorAuthConfirm() {
        this.passwordAttemptFail('');
        this.passwordValidating(true);

        if (this.passwordInput().length == 0) {
            this.passwordAttemptFail(messages.PasswordCantBeEmpty);
            this.passwordValidating(false);
            return;
        }
        try {
            if (await ctx.twoFactorAuthService.removeTwoFactorAuth({ password: this.passwordInput() })) {
                // update view ui
                
                this.authChoice(TwoFactorAuthTypes.None); // trigger external observers
                this.closePasswordChallenge();
                this.removedTwoFactorAuth(true);
                this.modifyChoiceInitialState = false;
            }
            else {
                this.passwordAttemptFail(messages.PasswordIsIncorrect);
            }
        }
        catch (e) {
            //console.log(e);
            alert('For security reasons, your account has been locked and you have been logged out.'); // need a new one
            //alert(messages.account);
            window.location.href = '/';
        }
        finally {
            this.passwordValidating(false);
        }
    }

    showPasswordChallenge() {
        $('#' + this.modalWindowId).modal('show');
    }

    closePasswordChallenge() {
        $('#' + this.modalWindowId).modal('hide');
    }

    PasswordConfirm_Text = ko.pureComputed(() => this.passwordValidating() ? 'Verifying...' : labels.Confirm);   // need new verifying.. resource
}